import {
  SendTransactionError,
  TransactionExpiredBlockheightExceededError,
  TransactionExpiredTimeoutError,
} from "@solana/web3.js"
import {
  InsufficientFundsError,
  PolychainError,
  TransactionRevertedError,
  TransactionTimedOutError,
  WalletRejectedError,
} from "./errors"
import { INSUFFICIENT_FUNDS_REGEX } from "./isInsufficientFundsError"
import { USER_REJECTED_REGEX } from "./isUserRejectedRequestError"

// Regex for identifying timeout-related transaction errors
const TIMEOUT_ERROR_REGEX =
  /(blockhash expired|block height exceeded|transaction has expired|timeout exceeded)/i

/**
 * Maps Solana web3.js errors to our internal Wallet errors
 * @param error - The error to parse
 * @returns A PolychainError instance
 */
export function parseSvmTransactionError(error: unknown): PolychainError {
  // If it's already a PolychainError, return it as is
  if (error instanceof PolychainError) {
    return error
  }

  // Check for user rejected transaction
  if (isSolanaUserRejectedError(error)) {
    return new WalletRejectedError(error as Error)
  }

  // Check for insufficient funds
  if (isSolanaInsufficientFundsError(error)) {
    return new InsufficientFundsError()
  }

  // Check for expired transaction errors
  if (isSolanaTransactionTimedOutError(error)) {
    return new TransactionTimedOutError(error as Error)
  }

  // Default to TransactionRevertedError for any other errors
  return new TransactionRevertedError(error as Error)
}

/**
 * Checks if the error is a user rejected transaction error
 */
function isSolanaUserRejectedError(error: unknown): boolean {
  const errorMessage = getErrorMessage(error)
  return USER_REJECTED_REGEX.test(errorMessage)
}

/**
 * Checks if the error is an insufficient funds error
 */
function isSolanaInsufficientFundsError(error: unknown): boolean {
  const errorMessage = getErrorMessage(error)
  return INSUFFICIENT_FUNDS_REGEX.test(errorMessage)
}

/**
 * Checks if the error is a transaction timed out error
 */
function isSolanaTransactionTimedOutError(error: unknown): boolean {
  if (
    error instanceof TransactionExpiredBlockheightExceededError ||
    error instanceof TransactionExpiredTimeoutError
  ) {
    return true
  }

  const errorMessage = getErrorMessage(error)

  return TIMEOUT_ERROR_REGEX.test(errorMessage)
}

/**
 * Extracts the error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof SendTransactionError) {
    // Extract the transaction error message
    const transactionError = error.transactionError
    return transactionError.message
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === "object" && error !== null) {
    // Try to extract message from error object
    const errorObj = error as Record<string, unknown>

    if (typeof errorObj.message === "string") {
      return errorObj.message
    }

    // Check for Solana RPC error structure
    if (
      typeof errorObj.error === "object" &&
      errorObj.error !== null &&
      typeof (errorObj.error as Record<string, unknown>).message === "string"
    ) {
      return (errorObj.error as Record<string, unknown>).message as string
    }
  }

  return String(error)
}
